import { FormField, isSome, useForwardedRef } from '@crocodille/common';
import cn from 'classnames';
import React, { ForwardedRef, forwardRef, useEffect } from 'react';
import { useCustomProperties } from '../../../hooks/use-custom-properties';

import styles from './input-area.module.scss';

export type InputAreaProps = Omit<
  React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >,
  'value' | 'onChange'
> & {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly invalid?: boolean;
  readonly size?: number;
};

const InputArea = forwardRef(
  (
    {
      className,
      value,
      onChange,
      invalid = false,
      size,
      ...props
    }: InputAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const [areaRef, forwardedRef] = useForwardedRef(ref);
    const setCustomProperty = useCustomProperties(areaRef);

    useEffect(() => {
      setCustomProperty('size', isSome(size) ? size.toString() : null);
    }, [setCustomProperty, size]);

    return (
      <textarea
        ref={forwardedRef}
        className={cn(
          styles.container,
          { [styles.isInvalid]: invalid },
          className,
        )}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...props}
      />
    );
  },
);

InputArea.displayName = 'InputArea';

export function fieldToInputAreaProps({
  value,
  onChange,
  onBlur,
  required,
  disabled,
  error,
}: FormField<string>): Pick<
  InputAreaProps,
  'value' | 'onChange' | 'onBlur' | 'required' | 'disabled' | 'invalid'
> {
  return {
    value,
    onChange,
    onBlur,
    required,
    disabled,
    invalid: isSome(error),
  };
}

export default InputArea;
